/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";

import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { GET_COUPONS } from "../graphql";
import config from "../config/config.json";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetCouponsMethods extends IPluginMethodMap {
  getCouponsProps(args: any): Promise<any>;
}

export class GetCouponsService implements IAgentPlugin {
  readonly methods: IGetCouponsMethods;

  constructor() {
    this.methods = {
      getCouponsProps: this.getCouponsProps.bind(this),
    };
  }

  async getCouponsProps(args: any) {
    console.log(`===============>argslog`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {};
    let comm_pre_id: any = "",
      presentationOptions: any = [],
      defaultPresentation: any = "";
    if (args?.criteria) {
      const searchType = this.determineSearchType(args?.criteria);
      if (searchType && searchType !== null) {
        if (searchType === "multi") {
          filter = this.getMultiFilter(args?.criteria);
        } else {
          filter = this.convertCriteria(args?.criteria);
        }
      }
    }
    defaultPresentation = { eq: args?.compProps?.defaultPresentations };
    filter = {
      ...filter,
      presentation: filter?.presentation ?? defaultPresentation,
    };

    try {
      const res = await client.query({
        query: GET_COUPONS,
        variables: {
          filters: filter,
          options: {
            limit: args?.size ?? 10,
            skip: args?.offset ?? 0,
            sort: {
              field: "createdAt",
              order: "desc",
            },
          },
        },
        fetchPolicy: "network-only",
      });
      const tableData = this.formatCouponsTable(res?.data?.getCoupons?.content);
      let _totalPages = res?.data?.getCoupons?.count
        ? Math.ceil(parseInt(res?.data?.getCoupons?.count) / 10)
        : 1;

      let _currentPage =
        res?.data?.getCoupons?.skip / res?.data?.getCoupons?.limit + 1;

      return {
        ultUsers: tableData?.length,

        config: { ...config, createdCoupons: { tableData } },
        // defaultPresentations:
        //   args?.criteria?.presentation ?? defaultPresentation,
        isEmpty: res?.data?.getCoupons?.content?.length <= 0,
        activePage: _currentPage,
        currentPage: _currentPage,
        pageSize: res?.data?.getCoupons?.limit,
        totalCount: res?.data?.getCoupons?.count,
        searchPresentationFilter: presentationOptions,
        // tableHeaders: this.updatePresentationOptions(
        //   settingConfig.tableHeaders,
        //   presentationOptions
        // ),
        totalPages: _totalPages,
      };
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatCouponsTable(data: any) {
    let _rowData: any = [];
    data.forEach((item: any) => {
      _rowData.push({
        id: item?.id,
        discountName: item?.name,
        discountType: item?.discountType,
        discountValue: item?.value,
        startDate: item?.startDate,
        endDate: item?.endDate,
        totalNumberofDiscounts: item?.usageLimit,
        presentationName: item?.presentation?.name,
        presentationId: item?.presentation?.id,
        section: item?.section?.name,
        sectionType: item?.sectionType?.name,
        couponSrc: item?.image,
        totalCoupons: item?.usageLimit,
        totalUsedCoupons: item?.usedCount,
        totalRemainingCoupons: item?.usageLimit - item?.usedCount,
        couponStatus: item?.status,
        discountCode: item?.couponCode,
        couponType: item?.couponType,
      });
    });
    return _rowData;
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      if (key === "status") {
        result[key] = criteria[key];
      } else if (key === "presentation") {
        result[key] = { eq: criteria[key] };
      } else {
        result[key] = { contains: criteria[key] };
      }
    }
    return result;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LL");
    }
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        transformedCriteria[inputData.id] = {
          [filterType.toLowerCase().replace("equal", "eq")]:
            filters[filterType],
        };
      }
    }
    return transformedCriteria;
  }
}
